/**
 * PremiumSelect.jsx
 * Select premium com suporte a multi-select, busca e grupos
 *
 * Features:
 * - Variantes: single, multiple (multi-select com chips)
 * - Opção searchable (filtro interno)
 * - Opção clearable (botão limpar)
 * - Suporte a grouped options (categorias)
 * - Tamanhos: sm, md, lg
 * - Estados: default, hover, focus, error, disabled
 * - Dark/Light mode automático via tokens
 *
 * Uso:
 *   <PremiumSelect
 *     label="Estado"
 *     value={selectedState}
 *     onChange={(value) => setSelectedState(value)}
 *     options={[
 *       { value: 'sp', label: 'São Paulo', group: 'Sudeste' },
 *       { value: 'rj', label: 'Rio de Janeiro', group: 'Sudeste' },
 *     ]}
 *     searchable
 *     clearable
 *   />
 */

import React, {
 useState, useRef, useMemo, useCallback, useEffect 
} from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Popper,
  Paper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  IconButton,
  Divider,
  Fade,
  CircularProgress,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import { usePremiumTokens } from '../../theme/usePremiumTokens';

/**
 * Componente principal PremiumSelect
 */
const PremiumSelect = React.forwardRef(({
  // Conteúdo
  label,
  placeholder = 'Selecione...',
  value,
  onChange,
  options = [],

  // Configuração
  multiple = false,
  searchable = false,
  clearable = false,
  grouped = false,
  size = 'md', // 'sm' | 'md' | 'lg'

  // Validação
  error = false,
  helperText,
  required = false,

  // Estado
  disabled = false,
  loading = false,

  // Layout
  fullWidth = true,
  maxHeight = 300,

  // Rendering customizado
  renderOption,
  renderValue,
  getOptionLabel = (opt) => opt?.label || opt?.name || String(opt?.value || ''),
  getOptionValue = (opt) => opt?.value ?? opt?.id ?? opt,
  getOptionGroup = (opt) => opt?.group,
  getOptionIcon = (opt) => opt?.icon,
  getOptionDisabled = (opt) => opt?.disabled,

  // Callbacks
  onOpen,
  onClose,
  onSearch,

  // Estilos
  sx: customSx,
  ...props
}, ref) => {
  const tokens = usePremiumTokens();
  const anchorRef = useRef(null);
  const searchInputRef = useRef(null);

  // Estado interno
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Safe token access helpers
  const fontSize = {
    xs: tokens?.typography?.fontSize?.xs || '12px',
    sm: tokens?.typography?.fontSize?.sm || '14px',
    md: tokens?.typography?.fontSize?.md || '16px',
    lg: tokens?.typography?.fontSize?.lg || '18px',
  };
  const fontWeight = {
    normal: tokens?.typography?.fontWeight?.normal || 400,
    medium: tokens?.typography?.fontWeight?.medium || 500,
    semibold: tokens?.typography?.fontWeight?.semibold || 600,
  };
  const letterSpacing = tokens?.typography?.letterSpacing?.wider || '0.05em';
  const radius = {
    sm: tokens?.radius?.sm || '4px',
    md: tokens?.radius?.md || '8px',
    lg: tokens?.radius?.lg || '12px',
    full: tokens?.radius?.full || '9999px',
  };
  const transitionAll = tokens?.transitions?.all || 'all 0.2s ease';

  // Surface tokens
  const surface = {
    glass: tokens?.surface?.glass || 'rgba(22, 27, 34, 0.5)',
    input: tokens?.surface?.input || 'rgba(22, 27, 34, 0.6)',
    inputHover: tokens?.surface?.inputHover || 'rgba(22, 27, 34, 0.7)',
    elevated: tokens?.surface?.elevated || 'rgba(22, 27, 34, 0.9)',
    hover: tokens?.surface?.hover || 'rgba(255, 255, 255, 0.05)',
    selected: tokens?.surface?.selected || 'rgba(59, 130, 246, 0.15)',
  };

  // Border tokens
  const border = {
    default: tokens?.border?.default || 'rgba(255, 255, 255, 0.08)',
    strong: tokens?.border?.strong || 'rgba(255, 255, 255, 0.12)',
    subtle: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)',
    errorSolid: tokens?.border?.errorSolid || '#ef4444',
    accentSolid: tokens?.border?.accentSolid || '#3b82f6',
  };

  // Shadow tokens
  const shadows = {
    focusRing: tokens?.shadows?.focusRing || '0 0 0 2px rgba(59, 130, 246, 0.3)',
    focusRingError: tokens?.shadows?.focusRingError || '0 0 0 2px rgba(239, 68, 68, 0.3)',
    lg: tokens?.shadows?.lg || '0 8px 24px rgba(0, 0, 0, 0.5)',
  };

  // Text tokens
  const text = {
    primary: tokens?.text?.primary || '#ffffff',
    secondary: tokens?.text?.secondary || 'rgba(255, 255, 255, 0.7)',
    placeholder: tokens?.text?.placeholder || 'rgba(255, 255, 255, 0.4)',
    muted: tokens?.text?.muted || 'rgba(255, 255, 255, 0.4)',
    error: tokens?.text?.error || '#ef4444',
  };

  // Accent tokens
  const accent = {
    primary: tokens?.accent?.primary || '#3b82f6',
    primaryHover: tokens?.accent?.primaryHover || '#2563eb',
    subtle: tokens?.accent?.subtle || 'rgba(59, 130, 246, 0.15)',
  };

  // Other tokens
  const semanticError = tokens?.semantic?.error || '#ef4444';
  const zIndexDropdown = tokens?.zIndex?.dropdown || 1300;

  // Configuração de tamanhos
  const sizeConfig = {
    sm: {
      height: 32,
      fontSize: fontSize.sm,
      padding: '4px 10px',
      chipSize: 'small',
      iconSize: 16,
    },
    md: {
      height: 36,
      fontSize: fontSize.md,
      padding: '6px 12px',
      chipSize: 'small',
      iconSize: 18,
    },
    lg: {
      height: 44,
      fontSize: fontSize.lg,
      padding: '10px 14px',
      chipSize: 'medium',
      iconSize: 20,
    },
  };

  const config = sizeConfig[size] || sizeConfig.md;

  // Filtra opções baseado na busca
  const filteredOptions = useMemo(() => {
    if (!searchTerm || !searchable) return options;

    const term = searchTerm.toLowerCase();
    return options.filter((opt) => {
      const label = getOptionLabel(opt).toLowerCase();
      const group = getOptionGroup(opt)?.toLowerCase() || '';
      return label.includes(term) || group.includes(term);
    });
  }, [options, searchTerm, searchable, getOptionLabel, getOptionGroup]);

  // Agrupa opções se necessário
  const groupedOptions = useMemo(() => {
    if (!grouped) return { '': filteredOptions };

    return filteredOptions.reduce((acc, opt) => {
      const group = getOptionGroup(opt) || '';
      if (!acc[group]) acc[group] = [];
      acc[group].push(opt);
      return acc;
    }, {});
  }, [filteredOptions, grouped, getOptionGroup]);

  // Flatten para navegação por teclado
  const flatOptions = useMemo(() => {
    return Object.values(groupedOptions).flat();
  }, [groupedOptions]);

  // Verifica se opção está selecionada
  const isSelected = useCallback((opt) => {
    const optValue = getOptionValue(opt);
    if (multiple) {
      return Array.isArray(value) && value.some((v) =>
        (typeof v === 'object' ? getOptionValue(v) : v) === optValue
      );
    }
    return (typeof value === 'object' ? getOptionValue(value) : value) === optValue;
  }, [value, multiple, getOptionValue]);

  // Handlers
  const handleOpen = () => {
    if (disabled || loading) return;
    setOpen(true);
    setSearchTerm('');
    setHighlightedIndex(-1);
    onOpen?.();

    // Foca no campo de busca se searchable
    setTimeout(() => {
      if (searchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
    onClose?.();
  };

  const handleSelect = (opt) => {
    const optValue = getOptionValue(opt);

    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const valueExists = currentValues.some((v) =>
        (typeof v === 'object' ? getOptionValue(v) : v) === optValue
      );

      if (valueExists) {
        // Remove
        onChange?.(currentValues.filter((v) =>
          (typeof v === 'object' ? getOptionValue(v) : v) !== optValue
        ));
      } else {
        // Adiciona
        onChange?.([...currentValues, opt]);
      }
    } else {
      onChange?.(opt);
      handleClose();
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.(multiple ? [] : null);
  };

  const handleRemoveChip = (opt, e) => {
    e.stopPropagation();
    if (!multiple || !Array.isArray(value)) return;

    const optValue = getOptionValue(opt);
    onChange?.(value.filter((v) =>
      (typeof v === 'object' ? getOptionValue(v) : v) !== optValue
    ));
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setHighlightedIndex(-1);
    onSearch?.(term);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        handleOpen();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < flatOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : flatOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && flatOptions[highlightedIndex]) {
          handleSelect(flatOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        handleClose();
        break;
      default:
        break;
    }
  };

  // Render do valor selecionado
  const renderSelectedValue = () => {
    if (renderValue) {
      return renderValue(value);
    }

    if (multiple && Array.isArray(value) && value.length > 0) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.25 }}>
          {value.map((v, idx) => {
            const opt = typeof v === 'object' ? v : options.find((o) => getOptionValue(o) === v);
            const uniqueKey = opt ? getOptionValue(opt) : (typeof v === 'object' ? v.id : v) || idx;
            return (
              <Chip
                key={uniqueKey}
                label={opt ? getOptionLabel(opt) : v}
                size={config.chipSize}
                onDelete={(e) => handleRemoveChip(opt || v, e)}
                deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                sx={{
                  height: size === 'sm' ? 20 : 24,
                  backgroundColor: accent.subtle,
                  color: accent.primary,
                  borderRadius: radius.sm,
                  '& .MuiChip-deleteIcon': {
                    color: accent.primary,
                    '&:hover': { color: accent.primaryHover },
                  },
                }}
              />
            );
          })}
        </Box>
      );
    }

    if (!multiple && value) {
      const opt = typeof value === 'object' ? value : options.find((o) => getOptionValue(o) === value);
      if (opt) {
        const icon = getOptionIcon(opt);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon && (
              <Box sx={{ display: 'flex', color: text.secondary }}>
                {icon}
              </Box>
            )}
            <Typography sx={{ fontSize: config.fontSize, color: text.primary }}>
              {getOptionLabel(opt)}
            </Typography>
          </Box>
        );
      }
    }

    return (
      <Typography sx={{ fontSize: config.fontSize, color: text.placeholder }}>
        {placeholder}
      </Typography>
    );
  };

  // Estilos base
  const baseStyles = {
    position: 'relative',
    width: fullWidth ? '100%' : 'auto',
    minWidth: 180,
  };

  const triggerStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: config.height,
    padding: config.padding,
    backgroundColor: disabled ? surface.glass : surface.input,
    border: `1px solid ${error ? border.errorSolid : open ? border.accentSolid : border.default}`,
    borderRadius: radius.md,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: transitionAll,
    outline: 'none',

    '&:hover': !disabled && {
      backgroundColor: surface.inputHover,
      borderColor: error ? border.errorSolid : border.strong,
    },

    '&:focus-within': !disabled && {
      borderColor: error ? border.errorSolid : border.accentSolid,
      boxShadow: error ? shadows.focusRingError : shadows.focusRing,
    },
  };

  const dropdownStyles = {
    backgroundColor: surface.elevated,
    border: `1px solid ${border.default}`,
    borderRadius: radius.lg,
    boxShadow: shadows.lg,
    overflow: 'hidden',
    mt: 0.5,
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
  };

  return (
    <Box sx={{ ...baseStyles, ...customSx }} ref={ref} {...props}>
      {/* Label */}
      {label && (
        <Typography
          component="label"
          sx={{
            display: 'block',
            fontSize: fontSize.sm,
            fontWeight: fontWeight.medium,
            color: error ? text.error : text.secondary,
            mb: 0.75,
          }}
        >
          {label}
          {required && (
            <Typography component="span" sx={{ color: semanticError, ml: 0.5 }}>
              *
            </Typography>
          )}
        </Typography>
      )}

      {/* Trigger */}
      <Box
        ref={anchorRef}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        sx={triggerStyles}
      >
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {renderSelectedValue()}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
          {/* Loading */}
          {loading && (
            <CircularProgress size={config.iconSize} sx={{ color: text.secondary }} />
          )}

          {/* Clear button */}
          {clearable && !disabled && !loading && (
            (multiple ? Array.isArray(value) && value.length > 0 : value) && (
              <IconButton
                size="small"
                onClick={handleClear}
                sx={{
                  p: 0.25,
                  color: text.secondary,
                  '&:hover': { color: text.primary, backgroundColor: surface.hover },
                }}
              >
                <CloseIcon sx={{ fontSize: config.iconSize }} />
              </IconButton>
            )
          )}

          {/* Arrow */}
          {!loading && (
            open ? (
              <KeyboardArrowUpIcon sx={{ fontSize: config.iconSize + 2, color: text.secondary }} />
            ) : (
              <KeyboardArrowDownIcon sx={{ fontSize: config.iconSize + 2, color: text.secondary }} />
            )
          )}
        </Box>
      </Box>

      {/* Helper text */}
      {helperText && (
        <Typography
          sx={{
            fontSize: fontSize.xs,
            color: error ? text.error : text.secondary,
            mt: 0.5,
          }}
        >
          {helperText}
        </Typography>
      )}

      {/* Dropdown */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        style={{
          zIndex: zIndexDropdown,
          width: anchorRef.current?.clientWidth || 'auto',
        }}
        modifiers={[
          { name: 'flip', enabled: true },
          { name: 'preventOverflow', enabled: true },
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Fade {...TransitionProps} timeout={150}>
              <Paper sx={dropdownStyles} elevation={0}>
                {/* Search */}
                {searchable && (
                  <Box sx={{ p: 1, borderBottom: `1px solid ${border.subtle}` }}>
                    <TextField
                      inputRef={searchInputRef}
                      value={searchTerm}
                      onChange={handleSearch}
                      placeholder="Buscar..."
                      size="small"
                      fullWidth
                      autoComplete="off"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: text.secondary }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: surface.input,
                          borderRadius: radius.md,
                          '& fieldset': { borderColor: border.subtle },
                          '&:hover fieldset': { borderColor: border.default },
                          '&.Mui-focused fieldset': { borderColor: accent.primary },
                        },
                        '& .MuiInputBase-input': {
                          fontSize: fontSize.sm,
                          color: text.primary,
                          py: 0.75,
                        },
                      }}
                    />
                  </Box>
                )}

                {/* Options list */}
                <List
                  sx={{
                    maxHeight,
                    overflow: 'auto',
                    py: 0.5,
                    '&::-webkit-scrollbar': { width: 6 },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: border.strong,
                      borderRadius: radius.full,
                    },
                  }}
                >
                  {filteredOptions.length === 0 ? (
                    <ListItem>
                      <ListItemText
                        primary="Nenhum resultado encontrado"
                        primaryTypographyProps={{
                          sx: { color: text.secondary, fontSize: fontSize.sm },
                        }}
                      />
                    </ListItem>
                  ) : (
                    Object.entries(groupedOptions).map(([group, groupOpts], groupIdx) => (
                      <React.Fragment key={group || 'ungrouped'}>
                        {/* Group header */}
                        {grouped && group && (
                          <ListSubheader
                            sx={{
                              backgroundColor: surface.elevated,
                              color: text.muted,
                              fontSize: fontSize.xs,
                              fontWeight: fontWeight.semibold,
                              textTransform: 'uppercase',
                              letterSpacing,
                              lineHeight: 2.5,
                              px: 1.5,
                            }}
                          >
                            {group}
                          </ListSubheader>
                        )}

                        {/* Group options */}
                        {groupOpts.map((opt, optIdx) => {
                          const optValue = getOptionValue(opt);
                          const optLabel = getOptionLabel(opt);
                          const optIcon = getOptionIcon(opt);
                          const optDisabled = getOptionDisabled(opt);
                          const selected = isSelected(opt);
                          const flatIdx = flatOptions.findIndex((o) => getOptionValue(o) === optValue);
                          const highlighted = flatIdx === highlightedIndex;

                          if (renderOption) {
                            return renderOption({
                              option: opt,
                              selected,
                              highlighted,
                              onClick: () => handleSelect(opt),
                            });
                          }

                          return (
                            <ListItem
                              key={optValue}
                              onClick={() => !optDisabled && handleSelect(opt)}
                              sx={{
                                py: 0.75,
                                px: 1.5,
                                cursor: optDisabled ? 'not-allowed' : 'pointer',
                                backgroundColor: highlighted
                                  ? surface.hover
                                  : selected
                                    ? surface.selected
                                    : 'transparent',
                                opacity: optDisabled ? 0.5 : 1,
                                '&:hover': !optDisabled && {
                                  backgroundColor: surface.hover,
                                },
                              }}
                            >
                              {/* Icon */}
                              {optIcon && (
                                <ListItemIcon sx={{ minWidth: 32, color: text.secondary }}>
                                  {optIcon}
                                </ListItemIcon>
                              )}

                              {/* Label */}
                              <ListItemText
                                primary={optLabel}
                                primaryTypographyProps={{
                                  sx: {
                                    fontSize: config.fontSize,
                                    color: selected ? accent.primary : text.primary,
                                    fontWeight: selected ? fontWeight.medium : fontWeight.normal,
                                  },
                                }}
                              />

                              {/* Check icon */}
                              {selected && (
                                <CheckIcon sx={{ fontSize: 18, color: accent.primary, ml: 1 }} />
                              )}
                            </ListItem>
                          );
                        })}

                        {/* Group divider */}
                        {grouped && groupIdx < Object.keys(groupedOptions).length - 1 && (
                          <Divider sx={{ my: 0.5, borderColor: border.subtle }} />
                        )}
                      </React.Fragment>
                    ))
                  )}
                </List>
              </Paper>
            </Fade>
          </ClickAwayListener>
        )}
      </Popper>
    </Box>
  );
});

PremiumSelect.displayName = 'PremiumSelect';

/**
 * MultiSelect - Wrapper para select múltiplo
 */
export const MultiSelect = React.forwardRef((props, ref) => (
  <PremiumSelect ref={ref} multiple {...props} />
));
MultiSelect.displayName = 'MultiSelect';

/**
 * SearchableSelect - Wrapper para select com busca
 */
export const SearchableSelect = React.forwardRef((props, ref) => (
  <PremiumSelect ref={ref} searchable {...props} />
));
SearchableSelect.displayName = 'SearchableSelect';

/**
 * GroupedSelect - Wrapper para select com grupos
 */
export const GroupedSelect = React.forwardRef((props, ref) => (
  <PremiumSelect ref={ref} grouped {...props} />
));
GroupedSelect.displayName = 'GroupedSelect';

export default PremiumSelect;
